import React from "react";
import { Card, CardContent, CardTitle } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/Tooltip";
import { clsx } from "clsx";

/**
 * DataCell Component
 *
 * A reusable card component designed to display key metrics and statistics in a consistent format.
 * Commonly used in dashboards to show data like counts, percentages, and comparative values.
 *
 * @example
 * ```tsx
 * <DataCell
 *   title="Community Members"
 *   tooltipText="Total number of students in your community"
 *   middleValue="2,070"
 *   bottomValue={
 *     <PercentChange
 *       value={5.2}
 *       customTemplate="{{percentChange}} from last cycle"
 *     />
 *   }
 *   icon={<Users />}
 * />
 * ```
 *
 * @example
 * ```tsx
 * <DataCell
 *   title="Advanced Tooltip"
 *   tooltipText={
 *     <div>
 *       <p>Main description</p>
 *       <ul>
 *         <li>Bullet point 1</li>
 *         <li>Bullet point 2</li>
 *       </ul>
 *     </div>
 *   }
 *   middleValue="1,234"
 * />
 * ```
 *
 * @param title - The label/title displayed at the top of the card
 * @param tooltipText - Tooltip content that appears on hover - accepts both string and JSX
 * @param middleValue - The primary metric value (typically a number or formatted string)
 * @param bottomValue - Additional context information, often a PercentChange component
 *                      for showing change over time with proper styling
 * @param icon - Optional icon element displayed in the top-right corner
 * @param isLoading - Shows skeleton loading state when true
 * @param className - Additional CSS classes for styling customization
 *
 * @remarks
 * The bottomValue prop commonly uses the PercentChange component to display
 * percentage changes with proper green/red styling and flexible templating.
 * When tooltipText is provided, the title will have a dotted underline and show
 * the tooltip on hover. The tooltipText prop now supports both strings and JSX content.
 */
interface DataCellProps {
  title?: React.ReactNode;
  tooltipText?: React.ReactNode;
  middleValue?: React.ReactNode;
  bottomValue?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  isLoading?: boolean;
}

export const DataCell = ({
  title,
  tooltipText,
  middleValue,
  bottomValue,
  icon,
  isLoading,
  className,
}: DataCellProps) => {
  if (isLoading) {
    return (
      <Card className={clsx("rounded-lg", className)} data-testid="skeleton">
        <CardContent className="px-4 py-6">
          {/* Title Section with Icon Skeleton */}
          <div className="mb-2 flex items-center justify-between">
            <Skeleton className="h-4 w-[80%]" />
            <Skeleton className="h-6 w-6 flex-shrink-0 rounded-full" />
          </div>

          {/* Middle Value Section Skeleton */}
          <div className="mb-2">
            <Skeleton className="h-6 w-[60%]" />
          </div>

          {/* Bottom Value Section Skeleton */}
          <div>
            <Skeleton className="h-3 w-[80%]" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasTooltip = Boolean(tooltipText);

  const titleContent = (
    <CardTitle
      className={clsx(
        "text-sm font-bold text-foreground",
        hasTooltip && "underline decoration-dotted decoration-from-font",
        hasTooltip &&
          "[text-decoration-thickness:10%] [text-underline-offset:0.2em] [text-decoration-skip-ink:none]",
        hasTooltip &&
          "hover:decoration-solid hover:[text-decoration-thickness:1px]",
      )}
    >
      {title}
    </CardTitle>
  );

  return (
    <Card className={clsx("rounded-lg", className)}>
      <CardContent className="px-4 py-6">
        {/* Title Section with Icon */}
        <div className="mb-2 flex items-center justify-between gap-1">
          {hasTooltip ? (
            <Tooltip>
              <TooltipTrigger asChild>{titleContent}</TooltipTrigger>
              <TooltipContent
                side="top"
                sideOffset={8}
                showArrow={false}
                className="max-w-sm px-3 py-2 text-wrap"
              >
                {tooltipText}
              </TooltipContent>
            </Tooltip>
          ) : (
            titleContent
          )}
          {icon && (
            <div className="flex items-center justify-end [&>svg]:size-4">
              {icon}
            </div>
          )}
        </div>

        {/* Middle Value Section */}
        <div className="mb-2">
          <div className="text-xl font-bold text-card-foreground">
            {middleValue}
          </div>
        </div>

        {/* Bottom Value Section */}
        <div className="inter-normal text-sm text-muted-foreground">
          {bottomValue}
        </div>
      </CardContent>
    </Card>
  );
};
